import { ArrowUpRight, Clock3, Wallet, Coins } from "lucide-react";

import { useAppSelector } from "@/store/hooks";
import { formatCurrency } from "@/utils/formatCurrency";
import { normalizeCurrency } from "@/utils";
import type { TransactionQuote } from "@/interfaces/transaction";
import { formatAmount } from "@/utils/formatAmount";

interface Props {
  loading: boolean;
  quote?: NoInfer<TransactionQuote> | undefined;
  assetSymbol?: string;
  networkName?: string;
}

const WithdrawalSummaryCard = ({
  loading,
  quote,
  assetSymbol,
  networkName,
}: Props) => {
  const { theme } = useAppSelector((state) => state.ui);

  if (loading) {
    return (
      <div
        className={`rounded-xl border p-5 ${
          theme === "dark"
            ? "border-border bg-muted"
            : "border-gray-200 bg-gray-50"
        }`}
      >
        <p className="text-sm text-muted-foreground">
          Calculating withdrawal...
        </p>
      </div>
    );
  }

  if (!quote) return null;

  const symbol = normalizeCurrency(assetSymbol ?? "");

  return (
    <div
      className={`rounded-xl border ${
        theme === "dark"
          ? "border-border bg-muted"
          : "border-gray-200 bg-gray-50"
      }`}
    >
      <div className="border-b border-border px-5 py-4">
        <h3 className="font-semibold">Withdrawal Summary</h3>

        <p className="text-xs text-muted-foreground mt-1">
          Please review the details before confirming this withdrawal.
        </p>
      </div>

      <div className="p-5 space-y-4">
        <SummaryRow
          icon={<ArrowUpRight className="w-4 h-4" />}
          label="You Send"
          // value={`${formatCurrency(quote.amount)} ${symbol}`}

          value={`${formatAmount({
            amount: quote.settlement.principal,
            isFiat: quote?.settlement?.debitCurrency?.isFiat,
            decimals: quote?.settlement?.debitCurrency?.decimals,
          })} ${symbol}`}
        />

        <SummaryRow
          icon={<Wallet className="w-4 h-4" />}
          label="Network"
          value={networkName ?? "-"}
        />

        <SummaryRow
          icon={<Coins className="w-4 h-4" />}
          label="Network Fee"
          value={`0.00 ${symbol}`}
        />

        <SummaryRow
          icon={<Coins className="w-4 h-4" />}
          label="Processing Fee"
          // value={`${formatCurrency(quote.processingFee)} ${symbol}`}
          value={`${formatAmount({
            amount: quote.settlement.feeAmount,
            isFiat: quote?.settlement?.debitCurrency?.isFiat,
            decimals: quote?.settlement?.debitCurrency?.decimals,
          })} ${symbol}`}
        />

        <SummaryRow
          icon={<Coins className="w-4 h-4" />}
          label="Total Fee"
          // value={`${formatCurrency(quote.totalFee)} ${symbol}`}
          value={` ${formatAmount({
            amount: quote.settlement.feeAmount,
            isFiat: quote?.settlement?.debitCurrency?.isFiat,
            decimals: quote?.settlement?.debitCurrency?.decimals,
          })}  ${symbol}`}
        />

        <div className="border-t border-border pt-4">
          <SummaryRow
            icon={<ArrowUpRight className="w-4 h-4" />}
            label="Recipient Receives"
            // value={`${formatCurrency(quote.receiveAmount)} ${symbol}`}
            value={`${formatAmount({
              amount: quote.settlement.principal,
              isFiat: quote?.settlement?.debitCurrency?.isFiat,
              decimals: quote?.settlement?.debitCurrency?.decimals,
            })}  ${symbol}`}
            highlight
          />
        </div>

        {/* {quote.estimatedArrival && (
          <SummaryRow
            icon={<Clock3 className="w-4 h-4" />}
            label="Estimated Arrival"
            value={quote.estimatedArrival}
          />
        )} */}

        {/* {quote.minimumAmount !== undefined && (
          <SummaryRow
            label="Minimum Withdrawal"
            value={`${formatCurrency(quote.minimumAmount)} ${symbol}`}
          />
        )} */}

        {/* {quote.maximumAmount !== undefined && (
          <SummaryRow
            label="Maximum Withdrawal"
            value={`${formatCurrency(quote.maximumAmount)} ${symbol}`}
          />
        )} */}
      </div>
    </div>
  );
};

interface SummaryRowProps {
  icon?: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
}

function SummaryRow({
  icon,
  label,
  value,
  highlight = false,
}: SummaryRowProps) {
  return (
    <div className="flex justify-between items-center gap-4">
      <div className="flex items-center gap-2">
        {icon}

        <span className="text-sm text-muted-foreground">{label}</span>
      </div>

      <span className={`font-medium ${highlight ? "text-emerald-500" : ""}`}>
        {value}
      </span>
    </div>
  );
}

export default WithdrawalSummaryCard;
